import {useEffect,useMemo,useState} from 'react';

type Location={code:string;name:string;timezone:string};
type Sale={id:string;occurredAt:string;customerId:string;customerName:string;
  description:string;tender:'CASH'|'STRIPE_TEST';totalCents:number};
type ResponseData={rows:Sale[];summary:{count:number;totalCents:number};
  scope:'PWA_CASH_AND_STRIPE_TEST';includesHistoricalHelm:false};
const money=(n:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD'}).format(n/100);
function today(timeZone:string){
  const parts=new Intl.DateTimeFormat('en-CA',{timeZone,year:'numeric',
    month:'2-digit',day:'2-digit'}).formatToParts(new Date());
  const item=(t:string)=>parts.find(x=>x.type===t)?.value??'';
  return item('year')+'-'+item('month')+'-'+item('day');
}
/** Sales primary navigation, distinct from drop-arrow's ticket actions.
 * Only verified PWA cash and Stripe TEST transactions are available.
 */
export function SalesReportWorkspace({location,locations,onChooseLocation,
  onCalendar,onNewSale}:{
  location:Location;locations:Location[];
  onChooseLocation:(code:string)=>void;onCalendar:()=>void;
  onNewSale:()=>void;
}){
  const current=useMemo(()=>today(location.timezone),[location.timezone]);
  const [from,setFrom]=useState(current);
  const [to,setTo]=useState(current);
  const [data,setData]=useState<ResponseData|null>(null);
  const [state,setState]=useState<'loading'|'ready'|'error'>('loading');
  const [error,setError]=useState('');
  const [exporting,setExporting]=useState(false);
  const q=useMemo(()=>new URLSearchParams({
    location:location.code,from,to}),[location.code,from,to]);
  useEffect(()=>{
    if(from>to){setState('error');setError('From must be on or before To.');return;}
    const controller=new AbortController();
    setState('loading');setError('');
    void fetch('/api/v1/staff/reports/sales?'+q,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async r=>{
      const v=await r.json() as {ok:boolean;data?:ResponseData;error?:{code?:string}};
      if(!r.ok||!v.ok||!v.data)throw Error(v.error?.code??'Sales report unavailable');
      if(!controller.signal.aborted){setData(v.data);setState('ready');}
    }).catch(e=>{if(!controller.signal.aborted){
      setState('error');
      setError(e instanceof Error?e.message:'Sales report unavailable');
    }});
    return ()=>controller.abort();
  },[q,from,to]);
  const exportCsv=async()=>{
    if(state!=='ready'||exporting)return;
    setExporting(true);setError('');
    try{
      const args=new URLSearchParams(q);args.set('format','csv');
      const res=await fetch('/api/v1/staff/reports/sales?'+args,{
        credentials:'same-origin',cache:'no-store'});
      if(!res.ok)throw Error('CSV report unavailable');
      const blob=await res.blob(),url=URL.createObjectURL(blob);
      const a=document.createElement('a');a.href=url;
      a.download='sales-from-'+from+'-to-'+to+'.csv';
      document.body.append(a);a.click();a.remove();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    }catch(e){setError(e instanceof Error?e.message:'CSV export unavailable');}
    finally{setExporting(false);}
  };
  return <section className="ecfs-reservations-report" aria-label="Sales Report">
    <header className="ecfs-reservation-report-title">
      <h2>Sales at East Coast Float Spa {location.name}</h2>
      <button type="button" onClick={onNewSale}>New Sale</button>
      <button type="button" onClick={onCalendar}>Back to Calendar</button>
    </header>
    <div className="ecfs-reservation-report-filters">
      <select aria-label="Sales report location" value={location.code}
        onChange={e=>onChooseLocation(e.target.value)}>
        {locations.map(x=><option key={x.code} value={x.code}>
          East Coast Float Spa {x.name}</option>)}
      </select>
      <label>From <input type="date" value={from}
        aria-label="Sales from date" onChange={e=>setFrom(e.target.value)}/></label>
      <label>To <input type="date" value={to}
        aria-label="Sales to date" onChange={e=>setTo(e.target.value)}/></label>
      <button type="button" onClick={()=>void exportCsv()}
        aria-label="Export Sales CSV" disabled={state!=='ready'||exporting}>
        {exporting?'Exporting…':'CSV ↓'}
      </button>
    </div>
    <p className="ecfs-report-accuracy">
      Verified PWA cash and Stripe TEST service/package sales only.
      Historical FloatHelm sales, memberships, gift cards, retail,
      refunds and unreconciled tax are not included in this subtotal.
    </p>
    {state==='loading'&&<p role="status">Loading sales…</p>}
    {state==='error'&&<p role="alert">{error}</p>}
    {state==='ready'&&data&&<>
      <div className="ecfs-reservation-report-stats">
        <div><strong>Sales</strong><span>{data.summary.count}</span></div>
        <div><strong>Verified total (partial scope)</strong>
          <span>{money(data.summary.totalCents)}</span></div>
      </div>
      <div className="ecfs-report-table-scroll">
        <table className="ecfs-reservation-report-table">
          <thead><tr><th>Paid at</th><th>Customer</th><th>Item</th>
            <th>Tender</th><th>Paid</th></tr></thead>
          <tbody>{data.rows.map(x=><tr key={x.id}>
            <td>{new Intl.DateTimeFormat('en-US',{dateStyle:'short',
              timeStyle:'short',timeZone:location.timezone}).format(new Date(x.occurredAt))}</td>
            <td>{x.customerName}</td><td>{x.description}</td>
            <td>{x.tender==='STRIPE_TEST'?'Stripe TEST':'Cash'}</td>
            <td>{money(x.totalCents)}</td>
          </tr>)}</tbody>
        </table>
        {!data.rows.length&&<p role="status">No completed PWA sales in this range.</p>}
      </div>
    </>}
    {error&&state==='ready'&&<p role="alert">{error}</p>}
  </section>;
}
